import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { CommonService } from '../../../../services/common/common.service';
import { ClassDurationsDataService, ClassDurationsColumnsService } from '../../../services';
import { ClassDuration, ClassDurationsResult } from '../../../interfaces';
import { DialogFilerComponent } from '../../filer/filer.component';

@Component({
  selector: 'app-class-duration-form',
  templateUrl: './class-duration-form.component.html',
  styleUrls: ['./class-duration-form.component.scss'],
  providers: [MessageService, ConfirmationService, DynamicDialogRef, DynamicDialogConfig],
})
export class ClassDurationFormComponent implements OnInit, OnDestroy, AfterViewInit {

  public classDuration: ClassDuration = {};
  public classDurationForm: FormGroup = new FormGroup({});
  public classDurations: ClassDuration[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public isLoading: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  public redirect = true;
  public disableSubmit = false;
  public enableFormAlert = true;

  private filerDialog: DynamicDialogRef = new DynamicDialogRef();

  constructor(
    private translate: TranslateService,
    public common: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private classDurationsDataService: ClassDurationsDataService,
    public classDurationsColumnsService: ClassDurationsColumnsService,
  ) {
  }

  ngOnInit(): void {
    console.log('ngOnInit', 'ClassDurationFormComponent');
    this.route.params.subscribe((params: any) => {
      if (typeof params['id'] !== 'undefined') {
        this.titleService.setTitle('Class Duration - Edit');
        this.getClassDuration(parseInt(params['id'], 10));
      }
    });
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.classDuration !== 'undefined') {
      this.classDuration = this.dynamicDialogConfig.data.classDuration;
    }
    if (typeof this.classDuration.id === 'undefined' || this.classDuration.id === null) {
      this.classDuration.id = null;
      this.classDuration.isPublished = false;
    }
    this.getClassDurations();
    this.prepareFrom();
  }

  ngOnDestroy(): void {
    console.log('ngOnDestroy', 'ClassDurationFormComponent');
  }

  ngAfterViewInit(): void {
    console.log('ngAfterViewInit', 'ClassDurationFormComponent');
  }

  getClassDuration(id: number): void {
    this.isLoading = true;
    this.classDurationsDataService.getClassDuration(id).subscribe((response: ClassDuration|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.classDuration = response;
        this.prepareFrom();
      }
    });
  }

  getClassDurations(): void {
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.classDurations !== 'undefined' && this.dynamicDialogConfig.data.classDurations.length > 0) {
      this.classDurations = this.dynamicDialogConfig.data.classDurations;
      return;
    }
    this.classDurationsDataService.getClassDurations(this.parameters).subscribe((response: ClassDurationsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.classDurations = response.results;
      }
    });
  }

  prepareFrom(): void {
    this.classDurationForm = new FormGroup({
      id: new FormControl(this.classDuration.id, []),
      name: new FormControl(this.classDuration.name, [Validators.required, ]),
      nameAr: new FormControl(this.classDuration.nameAr, []),
      nameFr: new FormControl(this.classDuration.nameFr, []),
      duration: new FormControl(this.classDuration.duration, [Validators.required, ]),
      icon: new FormControl(this.classDuration.icon, []),
      isPublished: new FormControl(this.classDuration.isPublished, [Validators.required, ]),
    });
  }

  list() {
    this.router.navigate(['/class-durations']);
  }

  showErrors() {
      this.showErrors();
      const invalidFields: string[] = [];
      Object.keys(this.classDurationForm.controls).forEach((field: any) => {
        const control = this.classDurationForm.get(field);
        if (control !== null) {
          control.markAsTouched({ onlySelf: true });
          if (control.invalid) {
            invalidFields.push(field);
          }
        }
      });
  }

  submitForm(redirect: boolean) {
    this.redirect = redirect;
    if (!this.classDurationForm.valid) {
      this.showErrors();
    } else {
      this.saveData(redirect);
    }
  }

  saveData(redirect: boolean) {
    this.classDuration = this.classDurationForm.value;
    this.disableSubmit = true;
    console.log('classDuration', this.classDuration);
    if (typeof this.classDuration.id !== 'undefined' && this.classDuration.id !== null) {
      this.add(redirect);
    } else {
      this.update(redirect);
    }
    return false;
  }

  add(redirect: boolean) {
    this.classDurationsDataService.updateClassDuration(this.classDuration).subscribe((data: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('CLASSDURATIONUPDATED')});
      if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
        this.dynamicDialogRef.close({refresh: true});
      } else if (redirect) {
        this.list();
      }
    }, (error: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CLASSDURATIONNOTUPDATED') + ' ' + error.message});
      console.warn(error);
    });
  }

  update(redirect: boolean) {
    this.classDurationsDataService.addClassDuration(this.classDuration).subscribe((data: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('CLASSDURATIONCREATED')});
        if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
          this.dynamicDialogRef.close({refresh: true});
        } else if (redirect) {
          this.list();
        }
      }, (error: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CLASSDURATIONNOTCREATED') + ' ' + error.message});
        console.warn(error);
    });
  }

  openFilerDialog(field: string): void {
    let url = '';
    const control = this.classDurationForm.get(field);
    if (control !== null) {
      url = control.value;
    }
    this.filerDialog = this.dialogService.open(DialogFilerComponent, {
      header: '', // this.translate.instant('FILEMANAGER'),
      width: '90%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1500,
      data: {
        path: 'class-durations',
        field: field,
        url: url,
      },
    });
    this.filerDialog.onClose.subscribe((response: any) => {
      if (response && response.selected) {
        const control = this.classDurationForm.get(field);
        if (control !== null) {
          control.setValue(response.selected.webPath);
        }
      }
    });
  }

}

@Component({
  selector: 'app-class-duration-form',
  templateUrl: './class-duration-form.component.html',
  styleUrls: ['./class-duration-form.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class DialogClassDurationFormComponent extends ClassDurationFormComponent {
}

